import { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { OpenStreetMapProvider } from "leaflet-geosearch";
import { MapPinIcon } from "@heroicons/react/20/solid";

const markerIcon = new L.Icon({
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

const DEFAULT_CENTER = [41.9028, 12.4964]; // Roma

const getAddressFromCoordinates = async (lat, lng) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "it",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Errore nel recupero dell'indirizzo");
    }

    const data = await response.json();
    return data?.display_name || "";
  } catch (error) {
    console.error("Errore nel reverse geocoding:", error);
    return "";
  }
};

function DraggableMarker({ position, onPositionUpdate }) {
  const eventHandlers = {
    dragend(e) {
      const { lat, lng } = e.target.getLatLng();
      onPositionUpdate(lat, lng);
    },
  };

  return position ? (
    <Marker
      position={position}
      icon={markerIcon}
      draggable
      eventHandlers={eventHandlers}
    />
  ) : null;
}

function MapUpdater({ position }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  return null;
}

function LocationEvents({ onPositionUpdate, onZoomChange }) {
  useMapEvents({
    click(e) {
      onPositionUpdate(e.latlng.lat, e.latlng.lng);
    },
    zoomend(e) {
      onZoomChange?.(e.target.getZoom());
    },
  });

  return null;
}

/**
 * Ricerca indirizzo + mappa + marker trascinabile + anteprima raggio (facoltativa).
 * Nessuna modale, nessun campo nome, nessun bottone salva/annulla: quelli
 * restano a carico di chi la utilizza.
 *
 * Contratto:
 * - position: [lat, lng] | null (controllato dal chiamante)
 * - onPositionChange([lat, lng]): chiamato IMMEDIATAMENTE ad ogni click/drag/ricerca,
 *   nessuna attesa di rete — il pin si muove subito.
 * - onAddressResolved(address): opzionale, chiamato in modo asincrono e con debounce
 *   (600ms) quando l'indirizzo leggibile è pronto. Sulla ricerca per testo viene
 *   chiamato subito con la label del risultato (nessun debounce necessario, è già
 *   un'azione esplicita dell'utente).
 * - radius: string | number | "" — se valorizzato mostra il cerchio in anteprima
 * - positionError: messaggio di errore da mostrare sotto la mappa
 */
const LocationMapField = ({
  position,
  onPositionChange,
  onAddressResolved,
  radius,
  initialCenter = DEFAULT_CENTER,
  initialZoom = 15,
  height = "100%",
  positionError,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [zoom, setZoom] = useState(initialZoom);
  const provider = new OpenStreetMapProvider();
  const addressTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (addressTimeoutRef.current) {
        clearTimeout(addressTimeoutRef.current);
      }
    };
  }, []);

  const scheduleAddressResolve = (lat, lng) => {
    if (!onAddressResolved) return;

    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
    }

    addressTimeoutRef.current = setTimeout(async () => {
      const address = await getAddressFromCoordinates(lat, lng);
      onAddressResolved(address);
    }, 600);
  };

  const handlePositionUpdate = (lat, lng) => {
    onPositionChange([lat, lng]);
    scheduleAddressResolve(lat, lng);
  };

  const handleSearch = async () => {
    const query = searchQuery.trim();

    if (!query) return;

    // Annulla eventuale reverse geocoding pendente
    if (addressTimeoutRef.current) {
      clearTimeout(addressTimeoutRef.current);
      addressTimeoutRef.current = null;
    }

    try {
      const results = await provider.search({ query });

      if (results.length > 0) {
        const result = results[0];
        onPositionChange([result.y, result.x]);

        // Mantiene il testo cercato, compreso il civico
        onAddressResolved?.(query);
      }
    } catch (error) {
      console.error("Errore nella ricerca dell'indirizzo:", error);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex mb-2">
        <input
          type="text"
          placeholder="Inserisci indirizzo..."
          className="border border-gray-300 p-2 flex-1 rounded-l-lg shadow-sm text-sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <button
          type="button"
          onClick={handleSearch}
          className="flex items-center px-3 py-1 text-sm font-semibold text-white shadow-sm justify-center bg-indigo-600 hover:bg-indigo-400 rounded-r-lg"
        >
          Cerca
        </button>
      </div>
      <div className="text-xs font-medium text-gray-600 pb-1">
        Inserisci l'indirizzo e il comune nel campo di ricerca. Per ottimizzare il posizionamento, seleziona e trascina il pin{" "}
        <MapPinIcon className="inline-block h-5 w-5 text-indigo-600" /> sulla
        mappa.
      </div>

      <div className="flex-grow" style={{ height }}>
        <MapContainer center={initialCenter} zoom={zoom} className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <MapUpdater position={position} />
          <LocationEvents
            onPositionUpdate={handlePositionUpdate}
            onZoomChange={setZoom}
          />
          <DraggableMarker
            position={position}
            onPositionUpdate={handlePositionUpdate}
          />
          {position && radius && (
            <Circle center={position} radius={parseInt(radius, 10)} color="blue" />
          )}
        </MapContainer>
      </div>

      {positionError && (
        <p className="text-red-500 text-xs mt-1">{positionError}</p>
      )}
    </div>
  );
};

export default LocationMapField;
